import { IfAbility } from '@efdigital/ll-addon-auth';
import formatDate, {
  DateFormat,
} from '@efdigital/ll-addon-content/helpers/format-date';
import type { CalendarItem } from '@efdigital/ll-addon-data';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import { queryCalendarItems } from '@efdigital/ll-admin-v2/data/builders/xceed/calendar-items';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';

interface ProgramsProgramCohortCalendarSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortRoute>;
    controller: unknown;
  };
}

function isAllDay(calendarItem: CalendarItem) {
  return calendarItem.dateType === 'date';
}

const ProgramsProgramCohortCalendarTemplate: TemplateOnlyComponent<ProgramsProgramCohortCalendarSignature> =
  <template>
    <div class="container py-5">
      {{pageTitle "Index"}}

      <IfAbility @ability="calendar" @atLeast="viewer">
        <:if>
          <Request
            @query={{queryCalendarItems @model.program_id @model.cohort_id}}
          >
            <:content as |calendarItems|>
              <div class="mb-4">
                <h2 class="gud-eyebrow mb-2">Calendar</h2>
                <ResultsTable @data={{calendarItems.data}}>
                  <:header>
                    <th>Title</th>
                    <th>Starts</th>
                    <th>Ends</th>
                    <th>Event Type</th>
                    <th>Location</th>
                    <th>Cancelled</th>
                  </:header>
                  <:row as |calendarItem|>
                    <tr>
                      <th scope="row"><LinkTo
                          @route="programs.program.cohort.calendar.calendar-item"
                          @models={{array
                            @model.program_id
                            @model.cohort_id
                            calendarItem.id
                          }}
                        >{{calendarItem.title}}</LinkTo></th>
                      <td>
                        {{#if (isAllDay calendarItem)}}
                          {{formatDate
                            calendarItem.starts
                            DateFormat.ShortDate
                          }}
                        {{else}}
                          {{formatDate
                            calendarItem.starts
                            DateFormat.ShortDate
                          }}
                          at
                          {{formatDate calendarItem.starts DateFormat.TimeOnly}}
                        {{/if}}
                      </td>
                      <td>
                        {{#if calendarItem.ends}}
                          {{#if (isAllDay calendarItem)}}
                            {{formatDate
                              calendarItem.ends
                              DateFormat.ShortDate
                            }}
                          {{else}}
                            {{formatDate
                              calendarItem.ends
                              DateFormat.ShortDate
                            }}
                            at
                            {{formatDate calendarItem.ends DateFormat.TimeOnly}}
                          {{/if}}
                        {{else}}
                          <span class="text-muted">&mdash;</span>
                        {{/if}}
                      </td>
                      <td>{{calendarItem.eventType}}</td>
                      <td>{{calendarItem.location}}</td>
                      <td>{{#if calendarItem.cancelled}}<span
                            class="badge bg-danger"
                          >Cancelled</span>{{else}}&mdash;{{/if}}</td>
                    </tr>
                  </:row>
                  <:empty>
                    <p>The calendar is empty.</p>
                  </:empty>
                </ResultsTable>
              </div>
            </:content>
            <:error as |error|>
              <p>Error: {{error.message}}</p>
            </:error>
          </Request>

          <IfAbility @ability="calendar" @atLeast="editor">
            <:if>
              <div class="d-flex justify-content-center mb-4">
                <div class="alert alert-info text-center" role="alert">
                  <strong>TODO:</strong>
                  Calendar item creation functionality needs to be implemented.
                  <br />
                  <small class="text-muted">This will allow creating new
                    calendar items for the cohort.</small>
                </div>
              </div>
            </:if>
          </IfAbility>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view calendar items.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>
    </div>
    {{outlet}}
  </template>;

export default ProgramsProgramCohortCalendarTemplate;
